/** @jsxImportSource @emotion/react */
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { Button as BSBtn, Spinner } from 'react-bootstrap';  // ← import Spinner
import { COLOR_PALLET } from '../ColorPalette';

const COLORS = {
  primary: COLOR_PALLET.PRIMARY,
  secondary: COLOR_PALLET.SECONDARY,
  danger: COLOR_PALLET.DANGER,
  success: COLOR_PALLET.SUCCESS,
};

const HOVER = {
  primary: COLOR_PALLET.PRIMARY_LIGHT,
  secondary: COLOR_PALLET.SECONDARY_LIGHT,
  danger: COLOR_PALLET.DANGER_LIGHT,
  success: COLOR_PALLET.SUCCESS_LIGHT,
};

const ACTIVE = {
  primary: COLOR_PALLET.PRIMARY_LIGHT,
  secondary: COLOR_PALLET.SECONDARY_LIGHT,
  danger: COLOR_PALLET.DANGER_LIGHT,
  success: COLOR_PALLET.SUCCESS_LIGHT,
};

const DISABLED = {
  primary: COLOR_PALLET.PRIMARY_LIGHT,
  secondary: COLOR_PALLET.SECONDARY_LIGHT,
  danger: COLOR_PALLET.DANGER_LIGHT,
  success: COLOR_PALLET.SUCCESS_LIGHT,
};

const variantStyles = ({ variant }) => {
  const parts = variant.split('-');
  const isOutline = parts[0] === 'outline';
  const key = isOutline ? parts[1] : parts[0];
  const color = COLORS[key];
  const hover = HOVER[key];
  const activeColor = ACTIVE[key];
  const disabled = DISABLED[key];

  if (!color) return '';

  if (isOutline) {
    return css`
      background-color: transparent;
      border: 1px solid ${color};
      color: ${color};

      &:hover:not(:disabled) {
        background-color: ${color};
        color: #FFF;
      }
      &:active:not(:disabled) {
        background-color: ${activeColor};
        border-color:   ${activeColor};
        color: #FFF;
      }
      &:disabled {
        border: 1px solid ${disabled}; 
        background-color: #FFF; 
        color: ${disabled};
        opacity: 0.65;
      }
    `;
  }

  // solid variant
  return css`
    background-color: ${color};
    border: 1px solid ${color};
    color: #fff;

    &:hover:not(:disabled) {
      background-color: ${hover};
      border-color: ${hover};
    }
    &:active:not(:disabled) {
      background-color: ${activeColor};
      border-color:   ${activeColor};
      color: #fff;
    }
    &:focus:not(:disabled) {
      background-color: ${activeColor};
      border-color:   ${activeColor};
      color: #fff;
    }
    &:disabled {
      background-color: ${disabled};
      border-color: ${disabled};
      opacity: 0.65;
    }
  `;
};

const sizeStyles = {
  sm: css`
    font-size: 0.875rem;
    line-height: 1.4;
    min-height:30px;
    height: 100%;
    padding: 4px 10px;
  `,
  md: css`
    font-size: 1rem;
    padding: 0.375rem 0.75rem;
    line-height: 1.5;
    min-height: 40px;
  `,
  lg: css`
    font-size: 1.125rem;
    padding: 0.5rem 1rem;
    line-height: 1.6;
    min-height: 48px;
  `,
};

const StyledButton = styled(BSBtn)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-family: system-ui, sans-serif;
  font-weight: 600;
  border-radius: 4px;
  transition: background-color 0.2s ease, border-color 0.2s ease,
    color 0.2s ease;
  width: ${({ fullWidth }) => (fullWidth ? '100%' : 'fit-content')};
  min-width: ${({ minWidth }) => minWidth};
  ${variantStyles}
  ${({ size }) => sizeStyles[size] || sizeStyles.md}
`;

const Button = ({
  variant = 'primary',
  size = 'md',
  isLoading = false,
  fullWidth = true,       // <-- default to true
  minWidth = 'auto',
  children,
  ...props
}) => (
  <StyledButton
    variant={variant}
    size={size}
    disabled={isLoading || props.disabled}
    fullWidth={fullWidth}
    minWidth={minWidth}
    {...props}
  >
    {isLoading ? (
      <Spinner
        as="span"
        animation="border"
        size='sm'
        role="status"
        aria-hidden="true"
      />
    ) : (
      children
    )}
  </StyledButton>
);

export default Button;
